import React from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { Card, Button, Typography, Row, Col, Space, Divider } from 'antd';
import { 
  ClockCircleOutlined, 
  TrophyOutlined, 
  CheckCircleOutlined,
  ArrowLeftOutlined 
} from '@ant-design/icons';
import { useResponsive } from '../hooks/useResponsive';
import { useExamQuestions } from '../hooks/exam';
import { useSecurityProtection } from '../hooks/useSecurityProtection';

const { Title, Text } = Typography;

interface ExamDetailsProps {}

const ExamDetails: React.FC<ExamDetailsProps> = () => {
  const { isMobile } = useResponsive();
  const navigate = useNavigate();
  const location = useLocation();
  const { examId } = useParams<{ examId: string }>();
  useSecurityProtection();
  
  // Get exam data from navigation state or use defaults
  const examData = location.state || {};
  const {
    examTitle = 'QM Exam',
    examDescription = 'test',
    totalMarks = 100,
    passMarks = 70,
    duration = 60,
    isActive = true
  } = examData;

  // Fetch exam questions using the hook
  const { data: examQuestions, isLoading: questionsLoading } = useExamQuestions(examId);

  const handleStartExam = () => {
    // Navigate to actual exam taking page
    navigate(`/exam/${examId}/take`, { 
      state: examData 
    });
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <div style={{ 
      maxWidth: '800px',
      margin: '0 auto',
      padding: isMobile ? '16px' : '24px'
    }}>
      {/* Header */}
      <div style={{ marginBottom: isMobile ? '20px' : '32px' }}>
        <Button 
          type="text" 
          icon={<ArrowLeftOutlined />}
          onClick={handleGoBack}
          style={{ 
            marginBottom: '16px',
            fontSize: isMobile ? '14px' : '16px'
          }}
        >
          Back to Dashboard
        </Button>
        
        <Title level={2} style={{ 
          margin: 0,
          fontSize: isMobile ? '24px' : '32px',
          color: '#1f1f1f'
        }}>
          {examTitle}
        </Title>
        
        {examDescription && (
          <Text style={{ 
            fontSize: isMobile ? '14px' : '16px',
            color: '#8c8c8c',
            display: 'block',
            marginTop: '8px'
          }}>
            {examDescription}
          </Text>
        )}
      </div>

      {/* Exam Info Card */}
      <Card 
        style={{ 
          borderRadius: '16px',
          border: 'none',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          marginBottom: isMobile ? '20px' : '24px'
        }}
      >
        <Title level={4} style={{ 
          marginBottom: isMobile ? '16px' : '20px',
          fontSize: isMobile ? '16px' : '18px'
        }}>
          Exam Information
        </Title>
        
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8}>
            <div style={{ textAlign: 'center', padding: isMobile ? '12px' : '16px' }}>
              <ClockCircleOutlined style={{ 
                fontSize: isMobile ? '24px' : '32px',
                color: '#1890ff',
                marginBottom: '8px'
              }} />
              <div>
                <Text strong style={{ 
                  display: 'block',
                  fontSize: isMobile ? '18px' : '24px',
                  color: '#1f1f1f'
                }}>
                  {duration}
                </Text>
                <Text style={{ 
                  fontSize: isMobile ? '12px' : '14px',
                  color: '#8c8c8c'
                }}>
                  Minutes
                </Text>
              </div>
            </div>
          </Col>
          
          <Col xs={24} sm={8}>
            <div style={{ textAlign: 'center', padding: isMobile ? '12px' : '16px' }}>
              <TrophyOutlined style={{ 
                fontSize: isMobile ? '24px' : '32px',
                color: '#faad14',
                marginBottom: '8px'
              }} />
              <div>
                <Text strong style={{ 
                  display: 'block',
                  fontSize: isMobile ? '18px' : '24px',
                  color: '#1f1f1f'
                }}>
                  {totalMarks}
                </Text>
                <Text style={{ 
                  fontSize: isMobile ? '12px' : '14px',
                  color: '#8c8c8c'
                }}>
                  Total Points
                </Text>
              </div>
            </div>
          </Col>
          
          <Col xs={24} sm={8}>
            <div style={{ textAlign: 'center', padding: isMobile ? '12px' : '16px' }}>
              <CheckCircleOutlined style={{ 
                fontSize: isMobile ? '24px' : '32px',
                color: '#52c41a',
                marginBottom: '8px'
              }} />
              <div>
                <Text strong style={{ 
                  display: 'block',
                  fontSize: isMobile ? '18px' : '24px',
                  color: '#1f1f1f'
                }}>
                  {passMarks}
                </Text>
                <Text style={{ 
                  fontSize: isMobile ? '12px' : '14px',
                  color: '#8c8c8c'
                }}>
                  Pass Marks
                </Text>
              </div>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Instructions Card */}
      <Card 
        style={{ 
          borderRadius: '16px',
          border: 'none',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          marginBottom: isMobile ? '20px' : '24px'
        }}
      >
        <Title level={4} style={{ 
          marginBottom: isMobile ? '16px' : '20px',
          fontSize: isMobile ? '16px' : '18px'
        }}>
          Instructions
        </Title>
        
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Text style={{ fontSize: isMobile ? '13px' : '14px' }}>
            • Read each question carefully before answering
          </Text>
          <Text style={{ fontSize: isMobile ? '13px' : '14px' }}>
            • You have {duration} minutes to complete the exam
          </Text>
          <Text style={{ fontSize: isMobile ? '13px' : '14px' }}>
            • You need at least {passMarks} points out of {totalMarks} to pass
          </Text>
          <Text style={{ fontSize: isMobile ? '13px' : '14px' }}>
            • Once started, the timer cannot be paused
          </Text>
          <Text style={{ fontSize: isMobile ? '13px' : '14px' }}>
            • Make sure you have a stable internet connection
          </Text>
          {examQuestions && (
            <Text style={{ fontSize: isMobile ? '13px' : '14px', fontWeight: '500' }}>
              • This exam contains {examQuestions.length} questions
            </Text>
          )}
        </Space>
      </Card>

      {/* Start Exam Button */}
      <div style={{ textAlign: 'center' }}>
        <Button 
          type="primary"
          size="large"
          onClick={handleStartExam}
          disabled={!isActive}
          style={{
            width: isMobile ? '100%' : 'auto',
            minWidth: isMobile ? 'auto' : '200px',
            height: isMobile ? '48px' : '56px',
            borderRadius: '12px',
            fontSize: isMobile ? '16px' : '18px',
            fontWeight: '600',
            backgroundColor: isActive ? '#52c41a' : undefined,
            borderColor: isActive ? '#52c41a' : undefined
          }}
        >
          {isActive ? 'Start Exam Now' : 'Exam Not Available'}
        </Button>
        
        {!isActive && (
          <Text style={{ 
            display: 'block',
            marginTop: '12px',
            fontSize: isMobile ? '12px' : '14px',
            color: '#ff4d4f'
          }}>
            This exam is currently not available
          </Text>
        )}
      </div>
    </div>
  );
};

export default ExamDetails;